import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuOutlined } from "@ant-design/icons";
import ProfileDropdown from "../../../components/ProfileDropdown";
import { denyAccess } from "../../../api/access";
import { logout, setAccess } from "../../../store/authSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";

/**
 * Горната лента: заглавието на страницата; вдясно профилът (Backoffice,
 * иконата за цял екран). „Към модулите" и логото са в страничното меню, а
 * на телефон бутонът ☰ го отваря — като страниците в „Конфигурация". Денят с
 * броя резервации и гости НАРОЧНО не се показва никъде отгоре — редът под
 * лентата само заемаше място. ‹ ден › и „+ Нова резервация" са в реда с
 * табовете над платното (`FloorToolbar`).
 */
const ReservationsTopBar = ({
  isNarrow,
  title,
  onOpenMobileMenu,
}: {
  isNarrow: boolean;
  // Заглавието на страницата (по подразбиране „Резервации").
  title?: string;
  onOpenMobileMenu: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const access = useAppSelector((state) => state.auth.access);
  const names = useAppSelector((state) => state.auth.names);
  const username = useAppSelector((state) => state.auth.username);

  // Ограниченият достъп се сваля (без изход), иначе — обикновен изход.
  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (error) {
        console.error("Deny access error:", error);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  return (
    <header
      className={`flex shrink-0 items-center justify-between gap-2.5 border-b border-shade-2 ${
        isNarrow ? "min-h-12 px-4 py-2.5" : "min-h-[60px] px-7 py-[11px]"
      } ${
        access
          ? "bg-[radial-gradient(ellipse_40%_120%_at_100%_50%,var(--color-brand-tint)_0%,transparent_70%),var(--color-surface)]"
          : "bg-surface"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {isNarrow && (
          <button
            type="button"
            onClick={onOpenMobileMenu}
            aria-label="Open menu"
            className="flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-[18px] text-ink"
          >
            <MenuOutlined />
          </button>
        )}
        <h1
          className={`m-0 min-w-0 truncate font-bold leading-[1.2] tracking-[-0.5px] ${
            access ? "text-brand-ink" : "text-ink"
          } ${isNarrow ? "text-[18px]" : "text-[22px]"}`}
        >
          {title ?? t("reservations.title")}
        </h1>
      </div>

      <div className="flex shrink-0 items-center">
        <ProfileDropdown
          displayName={names || username || ""}
          username={username ?? ""}
          access={access}
          isNarrow={isNarrow}
          onLogout={handleLogout}
        />
      </div>
    </header>
  );
};

export default ReservationsTopBar;
